/**
 * Popup creating / editing one variation print area of a PRINT product.
 *
 * The area being edited is a plain object (this.area) in the shape of the API:
 * every input writes into it at once (collect), so the form can be redrawn
 * when a rule or a technique is added or removed without losing what was typed.
 * Rules with no condition left are only dropped when sent.
 *
 * Sizes are in mm; width / height are the finished format (trim), without bleed.
 */
define('pimVariationPrintAreaEditView',[
  'module',
  'jquery',
  'underscore',
  'app',
  'settings',
  'dialogView',
  'template!pimVariationPrintAreaEditTpl'
], function (
  module,
  $,
  _,
  App,
  Settings,
  DialogView,
  viewTpl
) {
  'use strict';

  var LANGUAGES = ['fr', 'en', 'nl', 'de'];
  var SIDES = ['top', 'right', 'bottom', 'left'];
  var BORDERS = ['lostBorder', 'securityBorder'];
  var COLOR_MODES = ['quadri', 'quadriPlusSpot', 'spot', 'fixedFilling'];
  var ANY = '*';
  var PREVIEW_CODES_SHOWN = 12;

  /** Text in the user's language, else English, else the first one there is. */
  function translate(name) {
    var values = name && name.get ? name.get('values') : (name && name.values);
    if (!values) {
      return '';
    }
    var lang = (Settings.get('lang') || '').toLowerCase();
    var found = _.find(_.keys(values), function (key) {
      return key.toLowerCase() === lang && values[key];
    }) || _.find(_.keys(values), function (key) {
      return key.toLowerCase() === 'en' && values[key];
    }) || _.find(_.keys(values), function (key) {
      return !!values[key];
    });
    return found ? values[found] : '';
  }

  function toNumber(value) {
    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }
    var number = parseFloat(String(value).replace(',', '.'));
    return isNaN(number) ? null : number;
  }

  function border(value) {
    return {top: value, right: value, bottom: value, left: value};
  }

  function isUniform(b) {
    return !b || (b.top === b.right && b.top === b.bottom && b.top === b.left);
  }

  function errorMessage(xhr, fallback) {
    if (xhr && xhr.responseText && xhr.responseText.length < 500 && xhr.responseText.charAt(0) !== '<') {
      return xhr.responseText;
    }
    return fallback;
  }

  function newZone(isDefault) {
    return {
      imprintTechnology: {code: '', name: {values: {}}, colorModeType: 'quadri', maxNumberOfColors: 4},
      maxColors: 4,
      displayOrder: 0,
      isDefault: isDefault
    };
  }

  var VariationPrintAreaEditView = DialogView.extend({
    template: viewTpl,
    className: 'pim-vpa-edit',

    events: {
      'input .js-field': 'onFieldChange',
      'change .js-field': 'onFieldChange',
      'change .js-uniform': 'onRedrawChange',
      'click .js-add-rule': 'onAddRule',
      'click .js-remove-rule': 'onRemoveRule',
      'click .js-add-zone': 'onAddZone',
      'click .js-remove-zone': 'onRemoveZone',
      'click .js-preview': 'onPreview',
      'click .js-save': 'onSave',
      'click .js-cancel': 'onCancel'
    },

    initialize: function () {
      var area = this.options.area;
      if (!area) {
        area = {
          code: '',
          name: {values: {}},
          displayOrder: this.options.displayOrder || 0,
          width: null,
          height: null,
          lostBorder: border(3),
          securityBorder: border(5),
          appliesTo: [],
          variationCodes: [],
          printZones: [newZone(true)]
        };
        area.printZones[0].imprintTechnology.code = 'QUADRI';
      }
      area.name = area.name && area.name.values ? area.name : {values: {}};
      _.each(BORDERS, function (key) {
        var b = area[key] || {};
        area[key] = {top: toNumber(b.top) || 0, right: toNumber(b.right) || 0, bottom: toNumber(b.bottom) || 0, left: toNumber(b.left) || 0};
      });
      area.appliesTo = area.appliesTo || [];
      area.variationCodes = area.variationCodes || [];
      area.printZones = area.printZones || [];
      this.area = area;
      this.uniform = {
        lostBorder: isUniform(area.lostBorder),
        securityBorder: isUniform(area.securityBorder)
      };
      this.preview = null;
      this.error = null;
      this.busy = false;
      this.steps = this.buildSteps();
    },

    /** The option categories (configurator steps) and their options, in their order. */
    buildSteps: function () {
      var categories = this.options.pimProduct.get('data').get('productOptionCategories');
      return _.chain(categories ? categories.models : [])
        .sortBy(function (poc) {
          return poc.get('displayOrder') || 0;
        })
        .map(function (poc) {
          return {
            code: poc.get('code'),
            name: translate(poc.get('name')) || poc.get('code'),
            options: _.chain(poc.get('productOptions').models)
              .sortBy(function (po) {
                return po.get('displayOrder') || 0;
              })
              .map(function (po) {
                return {code: po.get('code'), name: translate(po.get('name')) || po.get('code')};
              })
              .value()
          };
        })
        .value();
    },

    serializeData: function () {
      var area = this.area;
      var lang = (Settings.get('lang') || 'en').toLowerCase();
      var steps = this.steps;
      // Conditions on a category the product no longer has stay visible, to be removed
      var unknown = _.map(area.appliesTo, function (rule) {
        return _.filter(_.keys(rule), function (cat) {
          return !_.findWhere(steps, {code: cat});
        });
      });
      return {
        area: area,
        languages: LANGUAGES,
        sides: SIDES,
        borders: BORDERS,
        uniform: this.uniform,
        colorModes: COLOR_MODES,
        steps: steps,
        unknown: unknown,
        any: ANY,
        variationCodesText: area.variationCodes.join(', '),
        zones: _.map(area.printZones, function (zone) {
          var tech = zone.imprintTechnology || {};
          var values = (tech.name && tech.name.values) || {};
          return {
            code: tech.code || '',
            name: values[lang] || translate(tech.name),
            colorModeType: tech.colorModeType || 'quadri',
            maxColors: zone.maxColors === null || zone.maxColors === undefined ? '' : zone.maxColors,
            isDefault: !!zone.isDefault,
            isNew: !zone.pimId
          };
        }),
        preview: this.preview,
        previewCodesShown: PREVIEW_CODES_SHOWN,
        error: this.error,
        busy: this.busy,
        isNew: !area.id
      };
    },

    onRender: function () {
      this.drawPreview();
    },

    // ── Form → this.area ────────────────────────────────────────────────

    collect: function () {
      var area = this.area;
      var $el = this.$el;
      var lang = (Settings.get('lang') || 'en').toLowerCase();

      area.code = ($el.find('.js-code').val() || '').trim();
      area.displayOrder = toNumber($el.find('.js-display-order').val()) || 0;
      area.width = toNumber($el.find('.js-width').val());
      area.height = toNumber($el.find('.js-height').val());

      $el.find('.js-name').each(function () {
        var value = (this.value || '').trim();
        var language = this.getAttribute('data-lang');
        if (value) {
          area.name.values[language] = value;
        } else {
          delete area.name.values[language];
        }
      });

      _.each(BORDERS, function (key) {
        var $uniform = $el.find('.js-uniform[data-border="' + key + '"]');
        var uniform = $uniform.length ? $uniform.is(':checked') : this.uniform[key];
        var wasUniform = this.uniform[key];
        if (wasUniform) {
          var all = toNumber($el.find('.js-border-all[data-border="' + key + '"]').val()) || 0;
          area[key] = border(all);
        } else {
          _.each(SIDES, function (side) {
            area[key][side] = toNumber($el.find('.js-border-side[data-border="' + key + '"][data-side="' + side + '"]').val()) || 0;
          });
        }
        if (uniform && !wasUniform) {
          // Back to one value for the four sides: the largest, never a smaller bleed
          area[key] = border(Math.max(area[key].top, area[key].right, area[key].bottom, area[key].left));
        }
        this.uniform[key] = uniform;
      }, this);

      area.appliesTo = [];
      $el.find('.js-rule').each(function () {
        var rule = {};
        $(this).find('.js-rule-cat').each(function () {
          if (this.value) {
            rule[this.getAttribute('data-cat')] = this.value;
          }
        });
        area.appliesTo.push(rule);
      });

      area.variationCodes = _.uniq(_.filter(($el.find('.js-variation-codes').val() || '').split(/[\s,;]+/), function (code) {
        return !!code;
      }));

      var defaultIndex = parseInt($el.find('.js-zone-default:checked').val(), 10);
      $el.find('.js-zone').each(function (index) {
        var zone = area.printZones[index];
        if (!zone) {
          return;
        }
        var $zone = $(this);
        var tech = zone.imprintTechnology = zone.imprintTechnology || {};
        tech.code = ($zone.find('.js-zone-code').val() || '').trim();
        tech.name = tech.name && tech.name.values ? tech.name : {values: {}};
        var name = ($zone.find('.js-zone-name').val() || '').trim();
        if (name) {
          tech.name.values[lang] = name;
        } else {
          delete tech.name.values[lang];
        }
        tech.colorModeType = $zone.find('.js-zone-mode').val();
        zone.maxColors = toNumber($zone.find('.js-zone-max').val());
        tech.maxNumberOfColors = zone.maxColors;
        zone.isDefault = index === defaultIndex;
        zone.displayOrder = index;
      });
    },

    /** What is sent: rules with no condition dropped. */
    payload: function () {
      var area = _.clone(this.area);
      area.appliesTo = _.filter(area.appliesTo, function (rule) {
        return _.keys(rule).length > 0;
      });
      return area;
    },

    onFieldChange: function () {
      this.collect();
      this.drawPreview();
    },

    onRedrawChange: function () {
      this.collect();
      this.render();
    },

    onAddRule: function () {
      this.collect();
      this.area.appliesTo.push({});
      this.render();
    },

    onRemoveRule: function (e) {
      this.collect();
      var index = parseInt($(e.currentTarget).closest('.js-rule').attr('data-index'), 10);
      this.area.appliesTo.splice(index, 1);
      this.render();
    },

    onAddZone: function () {
      this.collect();
      this.area.printZones.push(newZone(this.area.printZones.length === 0));
      this.render();
    },

    onRemoveZone: function (e) {
      this.collect();
      var index = parseInt($(e.currentTarget).closest('.js-zone').attr('data-index'), 10);
      var removed = this.area.printZones.splice(index, 1)[0];
      if (removed && removed.isDefault && this.area.printZones.length) {
        this.area.printZones[0].isDefault = true;
      }
      this.render();
    },

    // ── Drawing ─────────────────────────────────────────────────────────

    /**
     * The area at scale: the bleed as a red band around the finished format
     * (green line), the safe area inside it (blue dashes). Same colours as the editor.
     */
    drawPreview: function () {
      var $target = this.$el.find('.js-drawing');
      var area = this.area;
      var w = area.width, h = area.height;
      if (!w || !h || w <= 0 || h <= 0) {
        $target.html('<div class="pim-vpa-drawing-empty">' +
          _.escape(_.i18n('pim.variationPrintAreas.drawingEmpty', null, 'Enter the width and height to see the area')) + '</div>');
        return;
      }
      var lost = area.lostBorder, safe = area.securityBorder;
      var totalW = w + lost.left + lost.right, totalH = h + lost.top + lost.bottom;
      var safeW = w - safe.left - safe.right, safeH = h - safe.top - safe.bottom;
      var svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + totalW + ' ' + totalH + '" preserveAspectRatio="xMidYMid meet">' +
        '<rect x="0" y="0" width="' + totalW + '" height="' + totalH + '" fill="#e53935" fill-opacity="0.5"/>' +
        '<rect x="' + lost.left + '" y="' + lost.top + '" width="' + w + '" height="' + h + '" fill="#fff" stroke="#2e9d48" stroke-width="2" vector-effect="non-scaling-stroke"/>';
      if (safeW > 0 && safeH > 0) {
        svg += '<rect x="' + (lost.left + safe.left) + '" y="' + (lost.top + safe.top) + '" width="' + safeW + '" height="' + safeH + '" fill="none"' +
          ' stroke="#1e63d6" stroke-width="1.5" stroke-dasharray="6 4" vector-effect="non-scaling-stroke"/>';
      }
      svg += '</svg>';
      var legend = '<div class="pim-vpa-drawing-legend">' +
        '<span><i class="pim-vpa-swatch pim-vpa-swatch-lost"></i>' + _.escape(_.i18n('pim.variationPrintAreas.lostBorder', null, 'Bleed')) +
        ' ' + totalW + ' \u00d7 ' + totalH + ' mm</span>' +
        '<span><i class="pim-vpa-swatch pim-vpa-swatch-trim"></i>' + _.escape(_.i18n('pim.variationPrintAreas.format', null, 'Finished format')) +
        ' ' + w + ' \u00d7 ' + h + ' mm</span>' +
        '<span><i class="pim-vpa-swatch pim-vpa-swatch-safe"></i>' + _.escape(_.i18n('pim.variationPrintAreas.safeArea', null, 'Safe area')) +
        ' ' + (safeW > 0 && safeH > 0 ? safeW + ' \u00d7 ' + safeH + ' mm' : '\u2014') + '</span>' +
        '</div>';
      $target.html('<div class="pim-vpa-drawing-svg">' + svg + '</div>' + legend);
    },

    // ── Server calls ────────────────────────────────────────────────────

    onPreview: function () {
      this.collect();
      this.error = null;
      this.preview = {loading: true};
      this.render();
      $.when(App.request('pim:products:variation-print-area:preview', this.options.resProductId, this.payload()))
        .done(_.bind(function (result) {
          this.preview = {count: result ? result.count : 0, codes: (result && result.codes) || []};
          this.safeRender();
        }, this))
        .fail(_.bind(function (xhr) {
          this.preview = null;
          this.error = errorMessage(xhr, _.i18n('pim.variationPrintAreas.previewError', null, 'Unable to check the variations'));
          this.safeRender();
        }, this));
    },

    onSave: function () {
      if (this.busy) {
        return;
      }
      this.collect();
      this.error = null;
      this.busy = true;
      this.render();
      $.when(App.request('pim:products:variation-print-area:save', this.options.resProductId, this.payload()))
        .done(_.bind(function (saved) {
          this.busy = false;
          this.trigger('saved', saved);
          this.hide();
        }, this))
        .fail(_.bind(function (xhr) {
          this.busy = false;
          this.error = errorMessage(xhr, _.i18n('pim.variationPrintAreas.saveError', null, 'Unable to save the print area'));
          this.safeRender();
        }, this));
    },

    onCancel: function () {
      this.hide();
    },

    safeRender: function () {
      if (!this.isDestroyed) {
        this.render();
      }
    }
  });

  module.exports = VariationPrintAreaEditView;
});

